import React from "react";
import { toastr } from "react-redux-toastr";
import i18next from "i18next";
import { connect } from "react-redux";
import { FormGroup, FormControl, FormLabel } from "react-bootstrap";
import arrayMove from "array-move";
import { planViewsService } from "services";
import { errorFormating } from "utils/utils";
import ColumnList from "./PlanViewColumnList";
import SubmitButton from "components/SubmitButton";
import * as plansActions from "actions/plansActions";
import {
	buildColumnLabel,
	buildColumnsFromSources,
	buildPlanViewDetailPayload,
	getDefaultScopeApiValue,
	isSystemPlanView,
	mergeColumnsWithUserState,
	normalizeScopeForApi,
	resolvePlanViewScopes,
	translatePlanViewName,
} from "./planViewColumns";

class PlanViewEdit extends React.Component {
	constructor(props) {
		super(props);

		let planView = {
			name: "",
			details: [],
		};

		let isEdit = false;

		if (props.planView) {
			isEdit = true;
			planView = props.planView;
		}

		const scopes = resolvePlanViewScopes(props.plan_view_scopes);

		this.state = {
			isEdit,
			isLoading: false,
			submitted: false,
			errors: {},
			planView,
			columns: buildColumnsFromSources({
				rules: props.rules,
				accounts: props.accounts,
				planView,
				scopes,
			}),
		};
	}

	componentDidMount() {
		this.props.getPlanViewScopes();
		this.handleLanguageChanged = () => {
			this.props.getPlanViewScopes();
			this.syncColumnsFromProps();
		};
		i18next.on("languageChanged", this.handleLanguageChanged);
	}

	componentWillUnmount() {
		i18next.off("languageChanged", this.handleLanguageChanged);
	}

	componentDidUpdate(prevProps) {
		if (
			prevProps.rules !== this.props.rules ||
			prevProps.accounts !== this.props.accounts ||
			prevProps.plan_view_scopes !== this.props.plan_view_scopes
		) {
			this.syncColumnsFromProps();
		}
	}

	syncColumnsFromProps({ preserveUserState = true } = {}) {
		const scopes = resolvePlanViewScopes(this.props.plan_view_scopes);
		const built = buildColumnsFromSources({
			rules: this.props.rules,
			accounts: this.props.accounts,
			planView: this.props.planView || this.state.planView,
			scopes,
		});

		const columns = preserveUserState
			? mergeColumnsWithUserState(built, this.state.columns, scopes)
			: built;

		this.setState({ columns });
	}

	handleChange = (event) => {
		const { planView } = this.state;

		this.setState({
			planView: {
				...planView,
				[event.target.id]: event.target.value,
			},
		});
	};

	checkValidField = (name) => {
		return (
			this.state.submitted &&
			this.state.errors[name] !== undefined &&
			this.state.errors[name] !== ""
		);
	};

	checkItem = (obj1, obj2) => {
		return (
			(obj1.transaction_rule_id &&
				obj2.transaction_rule_id === obj1.transaction_rule_id) ||
			(obj1.account_id && obj2.account_id === obj1.account_id)
		);
	};

	prepareColumnsSubmit = () => {
		const { planView, columns } = this.state;
		const scopes = resolvePlanViewScopes(this.props.plan_view_scopes);

		const details = [];
		let order = 1;

		columns.forEach((column) => {
			if (!column.checked) {
				return;
			}

			details.push(
				buildPlanViewDetailPayload(
					{
						...column,
						order,
					},
					scopes
				)
			);
			order += 1;
		});

		planView.details = details;

		return planView;
	};

	handleSubmit = async (event) => {
		event.preventDefault();
		const { isEdit } = this.state;

		this.setState({ isLoading: true });
		let response = "";
		const props = this.props;

		const planView = this.prepareColumnsSubmit();

		if (isEdit) {
			response = await planViewsService.update(planView);
		} else {
			response = await planViewsService.add(planView);
		}

		if (response.ok) {
			this.setState(
				{
					isLoading: false,
					errors: {},
				},
				() => {
					toastr.success(i18next.t('common.success'), i18next.t('common.changesSaved'));
					this.props.getPlanViews();
					props.handleClose();
				}
			);
		} else {
			this.setState({
				isLoading: false,
				submitted: true,
				errors: errorFormating(response),
			});
		}
	};

	onSortEnd = ({ oldIndex, newIndex }) => {
		this.setState(({ columns }) => ({
			columns: arrayMove(columns, oldIndex, newIndex),
		}));
	};

	handleColumn = (item, event, data) => {
		if (data.action === "scope") {
			this.handleColumnScope(item, event);
		}
		if (data.action === "select") {
			this.handleColumnSelection(item, event);
		}
		if (data.action === "edit-mode") {
			this.handleColumnEditMode(item, event, data);
		}
		if (data.action === "label-edit") {
			this.handleColumnLabelEdit(item, event);
		}
	};

	handleColumnSelection = (item, event) => {
		const { columns } = this.state;
		const scopes = resolvePlanViewScopes(this.props.plan_view_scopes);
		const defaultScope = getDefaultScopeApiValue(scopes);
		const countSelected = columns.filter((o) => o.checked === true).length;
		const value = event.target.checked;

		if (countSelected >= 8 && value) {
			toastr.error(i18next.t('common.error'), i18next.t('planViews.maxColumnsError'));
		} else {
			this.setState({
				columns: this.state.columns.map((o) =>
					(item.transaction_rule_id &&
						o.transaction_rule_id === item.transaction_rule_id) ||
						(item.account_id && o.account_id === item.account_id)
						? {
							...o,
							checked: value,
							scope: value ? normalizeScopeForApi(o.scope, scopes) || defaultScope : o.scope,
							label: value && !o.customLabel
								? buildColumnLabel(
									o.baseName,
									normalizeScopeForApi(o.scope, scopes) || defaultScope,
									scopes
								)
								: o.label,
						}
						: o
				),
			});
		}
	};

	handleColumnScope = (item, event) => {
		const value = event.target.value;
		const scopes = resolvePlanViewScopes(this.props.plan_view_scopes);
		const baseName = item.baseName || item.label;
		const label = buildColumnLabel(baseName, value, scopes);

		this.setState({
			columns: this.state.columns.map((o) =>
				this.checkItem(item, o)
					? { ...o, scope: value, label, customLabel: false }
					: o
			),
		});
	};

	handleColumnEditMode = (item, event, data) => {
		const value = data.value;

		this.setState({
			columns: this.state.columns.map((o) =>
				this.checkItem(item, o) ? { ...o, edit: value } : o
			),
		});
	};

	handleColumnLabelEdit = (item, event) => {
		const value = event.target.value;

		this.setState({
			columns: this.state.columns.map((o) =>
				this.checkItem(item, o)
					? { ...o, label: value, customLabel: true }
					: o
			),
		});
	};

	render() {
		const { planView, errors, columns, isEdit } = this.state;
		const isSystemView = isEdit && isSystemPlanView(planView);
		const scopes = resolvePlanViewScopes(this.props.plan_view_scopes);

		return (
			<form onSubmit={this.handleSubmit}>
				<div className="modal-body plan-view-edit">
					<FormGroup className="form-group" controlId="name">
						<FormLabel>
							{i18next.t('planViews.labelName')} <span className="label-required">*</span>
						</FormLabel>
						<FormControl
							type="text"
							value={isSystemView ? translatePlanViewName(planView) : planView.name}
							onChange={isSystemView ? undefined : this.handleChange}
							readOnly={isSystemView}
							placeholder={i18next.t('planViews.labelName')}
							isInvalid={this.checkValidField("name")}
						/>
						<FormControl.Feedback type="invalid">
							{errors.name}
						</FormControl.Feedback>
					</FormGroup>

					<p className="text-info">{i18next.t('planViews.selectColumnsHint')}</p>

					<div className="column-wrap">
						<ColumnList
							helperClass="column-item-active"
							onSortEnd={this.onSortEnd}
							handleColumn={this.handleColumn}
							items={columns}
							scopes={scopes}
							useDragHandle
						/>
					</div>

					{errors.details && (
						<p className="invalid-feedback d-block">{errors.details}</p>
					)}
				</div>

				<div className="modal-footer">
					<button
						type="button"
						onClick={this.props.handleClose}
						className="btn btn-outline-primary"
					>
						{i18next.t('common.cancel')}
					</button>
					<SubmitButton
						type="submit"
						isLoading={this.state.isLoading}
						text={i18next.t('common.save')}
						loadingText={i18next.t('common.saving')}
					/>
				</div>
			</form>
		);
	}
}

const mapStateToProps = (reducers) => {
	return {
		...reducers.plansReducer,
		...reducers.rulesReducer,
	};
};

export default connect(mapStateToProps, plansActions)(PlanViewEdit);
